export const BIN_REORDER_LOCK_AWARENESS_FIELD = 'binReorderLock'

export interface BinReorderLock {
  binIds: string[]
  nodeIds?: string[]
  sceneId: string | null
}

interface AwarenessStateLike {
  currentCanvasSceneId?: unknown
  binReorderLock?: unknown
}

function isBinReorderLock(value: unknown): value is BinReorderLock {
  if (!value || typeof value !== 'object') return false
  const lock = value as Partial<BinReorderLock>
  return (
    Object.keys(lock).every(key => key === 'binIds' || key === 'nodeIds' || key === 'sceneId') &&
    (!('nodeIds' in lock) || (Array.isArray(lock.nodeIds) && lock.nodeIds.every(id => typeof id === 'string'))) &&
    Array.isArray(lock.binIds) &&
    lock.binIds.every(id => typeof id === 'string') &&
    (lock.sceneId === null || typeof lock.sceneId === 'string')
  )
}

/**
 * Awareness is advisory, but every connected client sees the same client IDs.
 * The lowest client ID wins overlapping locks deterministically, so two users
 * dragging in one auto-layout bin do not both commit whole-array childOrder
 * replacements. Disconnected clients disappear from Awareness automatically.
 */
export function resolveBinReorderLockWinner(
  states: ReadonlyMap<number, unknown>,
  binId: string,
  sceneId: string | null,
): number | null {
  return resolveDragLockWinner(states, 'binIds', binId, sceneId)
}

function resolveDragLockWinner(
  states: ReadonlyMap<number, unknown>,
  field: 'binIds' | 'nodeIds',
  id: string,
  sceneId: string | null,
): number | null {
  let winner: number | null = null
  for (const [clientId, rawState] of states) {
    if (!rawState || typeof rawState !== 'object') continue
    const state = rawState as AwarenessStateLike
    const lock = state.binReorderLock
    if (!isBinReorderLock(lock) || !lock[field]?.includes(id)) continue
    const stateSceneId = typeof state.currentCanvasSceneId === 'string' ? state.currentCanvasSceneId : null
    if (sceneId && lock.sceneId !== sceneId && stateSceneId !== sceneId) continue
    if (winner === null || clientId < winner) winner = clientId
  }
  return winner
}

export function canClientCommitBinReorder(
  states: ReadonlyMap<number, unknown>,
  clientId: number,
  binId: string,
  sceneId: string | null,
): boolean {
  const winner = resolveBinReorderLockWinner(states, binId, sceneId)
  return winner === null || winner === clientId
}

/** Node ownership shares the same awareness field and arbitration as bin reorders. */
export function canClientCommitNodeDrag(
  states: ReadonlyMap<number, unknown>,
  clientId: number,
  nodeId: string,
  sceneId: string | null,
): boolean {
  const winner = resolveDragLockWinner(states, 'nodeIds', nodeId, sceneId)
  return winner === null || winner === clientId
}
